import React from 'react'
import classnames from 'classnames'
import Image from 'next/image'
import { MaxWidthConstraint, Spacer } from '@revelrygroup/components'

import centsToDollars from '../../utils/centsToDollars'
import { FlowerProduct, MemorialTree, ShoppingCart } from '../../types'
import formatCurrency from '../../utils/formatCurrency'
import FlowerProductModel from '../models/FlowerProduct'
import MemorialTreeModel from '../models/MemorialTreeModel'
import styles from './styles.module.scss'
import CartModel from '../models/CartModel'

interface Props {
  includeEstimatedTotal?: boolean
  loading?: boolean
  onRemoveProduct?: (product: FlowerProduct) => Promise<ShoppingCart>
  onRemoveTreeProduct?: (product: MemorialTree) => Promise<ShoppingCart>
  productCodeProductMap: Record<string, FlowerProduct>
  shoppingCart: ShoppingCart
}

const CartProducts: React.FC<Props> = ({
  includeEstimatedTotal,
  loading,
  onRemoveProduct,
  onRemoveTreeProduct,
  productCodeProductMap,
  shoppingCart,
}) => {
  return (
    <>
      <div className={styles.wrapper}>
        {shoppingCart.flower_shopping_cart_items.map((product, idx) => {
          const flowerProduct = productCodeProductMap[product.external_id]

          if (!flowerProduct) return null

          return (
            <div className={styles.productWrapper} key={idx}>
              <div className={styles.left}>
                <div className={styles.imageWrapper}>
                  <Image
                    alt={product.name}
                    objectFit="cover"
                    layout="fill"
                    src={flowerProduct.images.small}
                    unoptimized
                  />
                </div>

                <div>
                  <MaxWidthConstraint isCentered={false} maxWidth={150}>
                    <p className="small">
                      {product.quantity}x {product.name}
                    </p>
                  </MaxWidthConstraint>

                  {!!onRemoveProduct && (
                    <button
                      className={styles.removeBtn}
                      disabled={loading}
                      onClick={() => onRemoveProduct(flowerProduct)}
                      type="button"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <MaxWidthConstraint isCentered={false} maxWidth={150}>
                <p className="small">${centsToDollars(product.price_in_cents)}</p>
              </MaxWidthConstraint>
            </div>
          )
        })}

        {shoppingCart.memorial_tree_shopping_cart_items.map((treeProduct) => (
          <div className={styles.productWrapper} key={treeProduct.product_id}>
            <div className={styles.left}>
              <div className={styles.treeImageWrapper}>
                <Image
                  alt={treeProduct.name}
                  objectFit="cover"
                  layout="fill"
                  src="/memorial-tree.jpg"
                  unoptimized
                />
              </div>

              <div>
                <MaxWidthConstraint isCentered={false} maxWidth={150}>
                  <p className="small">
                    {treeProduct.quantity}x {treeProduct.name}
                  </p>
                </MaxWidthConstraint>
                {!!onRemoveTreeProduct && (
                  <button
                    className={styles.removeBtn}
                    disabled={loading}
                    onClick={() => onRemoveTreeProduct(treeProduct)}
                    type="button"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <MaxWidthConstraint isCentered={false} maxWidth={150}>
              <p className="small">${centsToDollars(treeProduct.price_in_cents)}</p>
            </MaxWidthConstraint>
          </div>
        ))}
      </div>

      {includeEstimatedTotal && (
        <Spacer verticalTop="xs">
          <div className={styles.totalsWrapper}>
            <p className="small">Item Subtotal</p>

            <p className="small bold">
              {formatCurrency(
                FlowerProductModel.orderTotal(shoppingCart.flower_shopping_cart_items) +
                  MemorialTreeModel.productsSubtotalInDollars(
                    shoppingCart.memorial_tree_shopping_cart_items,
                  ),
              )}
            </p>
          </div>
        </Spacer>
      )}

      {!includeEstimatedTotal && (
        <>
          <Spacer verticalTop="xs">
            <div className={styles.totalsWrapper}>
              <p className="small">Subtotal</p>

              <p className="small">
                {formatCurrency(
                  CartModel.flowerSubtotalInDollars(shoppingCart) +
                    MemorialTreeModel.productsSubtotalInDollars(
                      shoppingCart.memorial_tree_shopping_cart_items,
                    ),
                )}
              </p>
            </div>
          </Spacer>

          <div className={classnames(styles.totalsWrapper)}>
            <p className="small">Tax</p>

            <p className="small">{formatCurrency(CartModel.taxInDollars(shoppingCart))}</p>
          </div>

          <div className={classnames(styles.totalsWrapper)}>
            <p className="small">Delivery Fee</p>

            <p className="small">
              {formatCurrency(CartModel.deliveryChargeInDollars(shoppingCart))}
            </p>
          </div>

          <Spacer verticalTop="xs">
            <div className={styles.totalsWrapper}>
              <p className="small">Total</p>

              <p className="small bold">
                {formatCurrency(
                  CartModel.flowerOrderTotalInDollars(shoppingCart) +
                    MemorialTreeModel.productsSubtotalInDollars(
                      shoppingCart.memorial_tree_shopping_cart_items,
                    ),
                )}
              </p>
            </div>
          </Spacer>
        </>
      )}
    </>
  )
}

export default CartProducts
